import React from "react";
import { Box, CircularProgress, Skeleton, Typography, alpha } from "@mui/material";
import {
  CheckCircleRounded as CheckedIcon,
  RadioButtonUncheckedRounded as UncheckedIcon,
} from "@mui/icons-material";
import { EmptyState } from "../../../atoms";
import SelectableListRow from "../../../molecules/SelectableListRow";
import { useClinicTheme } from "../../../../theme";
import { COLORS, TYPO } from "../../../../styles/uiConstants";

/**
 * Seçim drawer'larının kayan liste gövdesi: ilk yükleme iskeleti, boş durum,
 * seçilebilir satırlar ("Ekli" olanlar pasif) ve liste sonu durumu.
 * Veri `usePagedSearch` hook'undan gelir.
 *
 * @param {object} search - usePagedSearch dönüşü
 * @param {React.ReactElement} icon - Boş durum ikonu
 * @param {string} noun - Metinler için ("alerji", "ilaç" …)
 * @param {(o) => boolean} isSelected
 * @param {(o) => boolean} isAdded - Hastada/kayıtta zaten var
 * @param {(o) => void} onToggle
 * @param {boolean} [disabled]
 * @param {object[]} [leadItems] - Listenin başına eklenen satırlar (ör. serbest metin "… olarak ekle")
 */
export default function PagedOptionList({
  search,
  icon,
  noun,
  isSelected,
  isAdded,
  onToggle,
  disabled = false,
  leadItems = [],
}) {
  const { palette } = useClinicTheme();
  const tone = palette?.primary || COLORS.slate700;
  const { items, fetching, error, initialLoading, debouncedQuery } = search;

  let body;
  if (initialLoading) {
    body = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <Box key={i} sx={{ px: 3, py: 1.25, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={`${45 + (i % 3) * 15}%`} height={20} />
          <Skeleton variant="text" width={36} height={16} />
        </Box>
        <Skeleton variant="circular" width={18} height={18} />
      </Box>
    ));
  } else if (items.length === 0 && leadItems.length === 0) {
    const searching = Boolean(debouncedQuery);
    body = (
      <Box sx={{ pt: 4 }}>
        <EmptyState
          variant="illustrated-sm"
          icon={icon}
          title={error ? "Liste yüklenemedi" : searching ? "Sonuç bulunamadı" : `Tanımlı ${noun} yok`}
          description={
            error
              ? "Bağlantıyı kontrol edip aramayı yeniden deneyin."
              : searching
                ? `“${debouncedQuery}” aramanızla eşleşen ${noun} yok. Aramayı değiştirin.`
                : `Sistem değişkenlerine ${noun} eklendiğinde burada listelenir.`
          }
        />
      </Box>
    );
  } else {
    body = (
      <>
        {[...leadItems, ...items].map((o) => {
          const added = isAdded(o);
          const isOn = isSelected(o);
          return (
            <SelectableListRow
              key={o.key}
              active={isOn}
              disabled={added || disabled}
              onClick={() => onToggle(o)}
              title={o.title}
              subtitle={o.subtitle}
              sx={{ px: 3 }}
              trailing={
                added ? (
                  <Typography
                    sx={{
                      fontSize: TYPO.caption,
                      fontWeight: 600,
                      color: COLORS.slate500,
                      bgcolor: COLORS.slate100,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                    }}
                  >
                    Ekli
                  </Typography>
                ) : isOn ? (
                  <CheckedIcon sx={{ fontSize: 20, color: tone }} />
                ) : (
                  <UncheckedIcon sx={{ fontSize: 20, color: alpha(COLORS.slate400, 0.7) }} />
                )
              }
            />
          );
        })}
        {/* Liste sonu durumu: sonraki sayfa yükleniyor / hata */}
        <Box sx={{ py: 1.5, display: "flex", justifyContent: "center", minHeight: 44 }}>
          {fetching ? (
            <CircularProgress size={18} sx={{ color: COLORS.slate300 }} />
          ) : error ? (
            <Typography
              role="button"
              tabIndex={0}
              onClick={search.retry}
              sx={{ fontSize: TYPO.label, color: tone, fontWeight: 600, cursor: "pointer" }}
            >
              Devamı yüklenemedi · Tekrar dene
            </Typography>
          ) : null}
        </Box>
      </>
    );
  }

  return (
    <Box
      ref={search.scrollRef}
      onScroll={search.onScroll}
      sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", py: 0.5 }}
    >
      {body}
    </Box>
  );
}
